import { useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { PageShell } from "@/components/site-chrome";
import { Mail, Phone, Globe, Send, MessageSquare, PenTool, Shield, Users, HelpCircle } from "lucide-react";

export const Route = createFileRoute("/contact")({
  component: ContactPage,
});

const contactItems = [
  {
    icon: MessageSquare,
    title: "General Inquiries",
    email: "hello@weborbis.top",
    description: "Have a question, feedback, or story idea? We'd love to hear from you.",
    color: "bg-blue-50 border-blue-200 text-blue-600",
    iconBg: "bg-blue-100",
  },
  {
    icon: Users,
    title: "Press & Media",
    email: "press@weborbis.top",
    description: "Press credentials, interview requests, or media partnership opportunities.",
    color: "bg-green-50 border-green-200 text-green-600",
    iconBg: "bg-green-100",
  },
  {
    icon: Globe,
    title: "Advertising",
    email: "advertising@weborbis.top",
    description: "Reach football fans worldwide with targeted advertising solutions.",
    color: "bg-purple-50 border-purple-200 text-purple-600",
    iconBg: "bg-purple-100",
  },
  {
    icon: Shield,
    title: "Legal & Privacy",
    email: "legal@weborbis.top",
    description: "Questions about terms of service, privacy policy, or legal matters.",
    color: "bg-red-50 border-red-200 text-red-600",
    iconBg: "bg-red-100",
  },
  {
    icon: PenTool,
    title: "Submissions",
    email: "submissions@weborbis.top",
    description: "Submit your football writing or analysis for consideration.",
    color: "bg-orange-50 border-orange-200 text-orange-600",
    iconBg: "bg-orange-100",
  },
  {
    icon: HelpCircle,
    title: "Technical Support",
    email: "support@weborbis.top",
    description: "Report website issues, errors, or technical problems.",
    color: "bg-cyan-50 border-cyan-200 text-cyan-600",
    iconBg: "bg-cyan-100",
  },
];

function ContactPage() {
  useEffect(() => {
    document.title = "Contact Weborbis Sport — Football News";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute("content", "Get in touch with Weborbis Sport. Contact us for press inquiries, advertising, partnerships, or general feedback about our football coverage.");
    }
  }, []);

  return (
    <PageShell>
      <div className="relative overflow-hidden bg-brand py-16 md:py-20">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-72 h-72 bg-black rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-black rounded-full blur-3xl translate-x-1/3 translate-y-1/3"></div>
        </div>
        <div className="container-page relative">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-extrabold text-ink tracking-tight">
              Get in Touch
            </h1>
            <p className="mt-4 text-lg text-ink/80 max-w-xl">
              We'd love to hear from you! Whether you have a story idea, feedback, or business
              inquiry, our team is ready to assist.
            </p>
          </div>
        </div>
      </div>

      <div className="container-page py-12 md:py-16">
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {contactItems.map((item) => {
            const Icon = item.icon;
            return (
              <div
                key={item.title}
                className={`group p-6 rounded-xl border-2 ${item.color} transition-all duration-300 hover:scale-[1.02] hover:shadow-lg`}
              >
                <div className={`inline-flex p-3 rounded-lg ${item.iconBg} mb-4`}>
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                <p className="text-sm opacity-80 mb-4 leading-relaxed">{item.description}</p>
                <a
                  href={`mailto:${item.email}`}
                  className="inline-flex items-center gap-2 font-semibold hover:underline"
                >
                  <Mail className="w-4 h-4" />
                  {item.email}
                </a>
              </div>
            );
          })}
        </div>

        <div className="mt-16 max-w-2xl mx-auto">
          <div className="bg-card border border-border rounded-2xl p-8 md:p-10 shadow-xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-brand rounded-lg">
                <Send className="w-6 h-6 text-ink" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Send us a Message</h2>
                <p className="text-sm text-muted-foreground">We'll respond within 24 hours</p>
              </div>
            </div>

            <form className="space-y-5">
              <div className="grid gap-5 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium mb-2">Name</label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Email</label>
                  <input
                    type="email"
                    className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Subject</label>
                <input
                  type="text"
                  className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                  placeholder="How can we help you?"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Message</label>
                <textarea
                  rows={5}
                  className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors resize-none"
                  placeholder="Write your message here..."
                ></textarea>
              </div>

              <button
                type="submit"
                className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-ink text-white font-semibold rounded-lg hover:bg-ink/90 transition-colors"
              >
                <Send className="w-5 h-5" />
                Send Message
              </button>
            </form>
          </div>
        </div>

        <div className="mt-16 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand/10 rounded-full border border-brand/30">
            <Phone className="w-4 h-4 text-brand-foreground" />
            <span className="text-sm font-medium text-brand-foreground">
              Weborbis Sport — weborbis.top
            </span>
          </div>
        </div>
      </div>
    </PageShell>
  );
}